import classNames from "classnames";
import { Entry, EntrySkeletonType, UnresolvedLink } from "contentful";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { AbstractIntlMessages, useTranslations } from "next-intl";

import { useBreakpoint } from "~/contexts/breakpoint";
import { useCountryDialog } from "~/contexts/country-dialog-context";
import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";
import contentfulUtils from "~/utils/contentful-utils";
import crossCountryUtils from "~/utils/cross-country-utils";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";
import textUtils from "~/utils/text-utils";
import useScrollDirection from "~/utils/use-scroll-direction";

import Container from "../common/container";
import Icon from "../common/icon";
import styles from "./header.module.scss";

type Props = {
  entry: UnresolvedLink<"Entry"> | Entry<EntrySkeletonType>;
  navColor: "white" | "gradient" | "transparent";
  inPpd?: boolean;
  searchSuggestions?: string[];
  isHomepage?: boolean;
  messages?: AbstractIntlMessages;
};

export default function Header(props: Props) {
  const t = useTranslations();
  const router = useRouter();
  const breakpoint = useBreakpoint();
  const otherScrollDetector = useScrollDirection({});
  const { dir, y } = useScrollDirection({
    initialDirection: "up",
    thresholdPixels: Constants.HEADER_HEIGHT,
  });

  const country = crossCountryUtils.getCurrentCountryCode(router);
  const { open } = useCountryDialog();

  if (!isActiveEntry(props.entry)) {
    return null;
  }

  assertEditorialType<Editorials.Nav>(props.entry, ["navigation"]);

  const storeLink = isActiveEntry(props.entry.fields.storeLink)
    ? props.entry.fields.storeLink
    : undefined;
  const inspectorModeStoreLink = contentfulUtils.useInspectorMode(storeLink);

  return (
    <header
      id="header"
      className={classNames(
        styles.header,
        { [styles.scrolledTotal]: dir == "down" },
        { [styles.scrolledTop]: y >= Constants.HEADER_HEIGHT },
        { [styles.scrolledNavBg]: otherScrollDetector.y >= 1 },
        { [styles.inPdp]: props.inPpd },
        styles[props.navColor],
      )}
    >
      {/* <TopBar entry={props.entry.fields.topBar} /> */}
      <div className={styles.main}>
        <Container className={styles.mainContainer}>
          {storeLink && (
            <Link
              {...inspectorModeStoreLink?.getProps("text")}
              prefetch={false}
              href={textUtils.sanitizeContentfulUrl(storeLink, router)}
              target={storeLink.fields.openOnANewTab ? "_blank" : undefined}
              className={styles.topBarLink}
            >
              {storeLink.fields.icon &&
                isActiveEntry(storeLink.fields.icon) && (
                  <Icon name={storeLink.fields.icon.fields.svgIconId} />
                )}
              <span>{storeLink.fields.text}</span>
            </Link>
          )}
          <div className={styles.logoWrapper}>
            {props.isHomepage ? (
              <h1>
                <Link href="/" className={styles.center}>
                  <Icon name="kiko" height={breakpoint == "mobile" ? 24 : 28} />
                </Link>
              </h1>
            ) : (
              <Link href="/" className={styles.center}>
                <Icon name="kiko" height={breakpoint == "mobile" ? 24 : 28} />
              </Link>
            )}
          </div>
          <button className={styles.topBarLink} onClick={open}>
            <>
              <span>
                {router.locale
                  ?.replace(/(\w*)\s*-\s*(\w*)/, "$2 | $1")
                  .toUpperCase()}
              </span>
              <Image
                unoptimized
                width={16}
                height={16}
                alt={country}
                src={`/next/assets/flags/${country.toLowerCase()}.svg`}
              />
            </>
          </button>
        </Container>
      </div>
    </header>
  );
}
